import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppRouter from './AppRouter';
import LoadingSpinner from './components/LoadingSpinner';
import Login from './pages/Login';
import { getApiUrl } from './config/api';
import { isBookingDomain, getAppBranding } from './config/portalMode';
import {
    fetchDealerships, 
    fetchChecklistTemplate, 
    fetchFactfindTemplate, 
    fetchLinksTemplate, 
    fetchContactPlanTemplate, 
    fetchSignoffTemplate,
    fetchDropdownOptions,
    updateDealership,
    updateTemplate,
    addDealership,
    updateDropdownOptions,
    pushTemplateToDealerships
} from './services/apiService';

const AppContent = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [role, setRole] = useState(null);
    const [dealerships, setDealerships] = useState([]);
    const [templates, setTemplates] = useState({});
    const [dropdownOptions, setDropdownOptions] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticating, setIsAuthenticating] = useState(true);

    useEffect(() => {
        const branding = getAppBranding();
        document.title = branding.name;
    }, []);

    // Check authentication on mount
    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('accessToken');
            console.log('App.js - Checking auth, token exists:', !!token);
            if (!token) {
                setIsAuthenticating(false);
                return;
            }

            try {
                const response = await fetch(`${getApiUrl()}/auth/me`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                console.log('App.js - Auth check response status:', response.status);

                if (response.ok) {
                    const data = await response.json();
                    console.log('App.js - User authenticated:', data.user?.email, 'Role:', data.user?.role, 'HasJobListAccess:', data.user?.hasJobListAccess, 'HasSetupAccess:', data.user?.hasSetupAccess);
                    setUser(data.user);
                    setRole(data.user.role);
                } else {
                    const errorData = await response.json().catch(() => ({}));
                    console.error('App.js - Auth check failed:', response.status, errorData);
                    // Token invalid, clear it
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    setIsAuthenticating(false);
                }
            } catch (error) {
                console.error('App.js - Auth check error:', error);
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                setIsAuthenticating(false);
            } finally {
                setIsAuthenticating(false);
            }
        };

        checkAuth();
    }, []);

    useEffect(() => {
        if (!role) {
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                setIsLoading(true);
                const [
                    loadedDealerships, 
                    checklistTemplate, 
                    factfindTemplate, 
                    linksTemplate, 
                    contactPlanTemplate, 
                    signoffTemplate, 
                    loadedDropdownOptions
                ] = await Promise.all([
                    fetchDealerships(),
                    fetchChecklistTemplate(),
                    fetchFactfindTemplate(),
                    fetchLinksTemplate(),
                    fetchContactPlanTemplate(),
                    fetchSignoffTemplate(),
                    fetchDropdownOptions(),
                ]);

                setDealerships(loadedDealerships || []);
                setTemplates({
                    checklist: checklistTemplate || { sales: [], service: [] },
                    factfind: factfindTemplate || { sales: [], service: [] },
                    links: linksTemplate || { sales: [], service: [] },
                    contactPlan: contactPlanTemplate || { sales: [], service: [] },
                    signoff: signoffTemplate || { 
                        sales: { messageTypes: [], locations: [], assignments: {} },
                        service: { messageTypes: [], locations: [], assignments: {} }
                    }
                });
                setDropdownOptions(loadedDropdownOptions || {});
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                setIsLoading(false);
            }
        };
        fetchData();
    }, [role]);

    const handleLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setUser(null);
        setRole(null);
        navigate('/login');
    };

    const handleUpdateDealership = async (updated) => {
        try {
            const savedDealership = await updateDealership(updated);
            setDealerships(prev => prev.map(d => d.id === savedDealership.id ? savedDealership : d));
        } catch (error) {
            console.error("Failed to update dealership:", error);
            // Optionally: show an error message to the user
        }
    };

    const handleTemplateUpdate = async (key, updated) => {
        try {
            const savedTemplate = await updateTemplate(key, updated);
            setTemplates(prev => ({ ...prev, [key]: savedTemplate }));
            
            // For factfind and links, refresh from API to get latest fields
            if (key === 'factfind' || key === 'links') {
                try {
                    if (key === 'factfind') {
                        const freshTemplate = await fetchFactfindTemplate();
                        setTemplates(prev => ({ ...prev, factfind: freshTemplate }));
                    } else if (key === 'links') {
                        const freshTemplate = await fetchLinksTemplate();
                        setTemplates(prev => ({ ...prev, links: freshTemplate }));
                    }
                } catch (refreshError) {
                    console.error(`Failed to refresh ${key} template:`, refreshError);
                }
            }
        } catch (error) {
            console.error(`Failed to update template ${key}:`, error);
        }
    };

    const handleAddDealership = async (name) => {
        try {
            const newDealership = await addDealership(name);
            setDealerships(p => [...p, newDealership]);
        } catch (error) {
            console.error("Failed to add dealership:", error);
        }
    };

    const handleDropdownUpdate = async (updated) => {
        try {
            const savedOptions = await updateDropdownOptions(updated);
            setDropdownOptions(savedOptions);
        } catch (error) {
            console.error("Failed to update dropdown options:", error);
        }
    };

    const handlePushTemplate = async (moduleKey, dealershipIds) => {
        try {
            // The API performs the update. We then refetch the dealership list 
            // to ensure the UI is in sync with the database.
            await pushTemplateToDealerships(moduleKey, dealershipIds);
            const reloadedDealerships = await fetchDealerships();
            setDealerships(reloadedDealerships || []);
        } catch (error) {
            console.error(`Failed to push template ${moduleKey}:`, error);
        }
    };

    // Allow auth callback to process before checking authentication
    const isAuthCallback = window.location.pathname === '/auth/callback';
    const isLoginPage = window.location.pathname === '/login';
    
    // Check if this is a public event route (no auth required)
    const isPublicEventRoute = window.location.pathname.startsWith('/events/public/');
    
    // Check if we're on a custom hostname at root path (public event access via custom domain)
    const hostname = window.location.hostname;
    const defaultDomain = process.env.REACT_APP_DEFAULT_DOMAIN || 'localhost';
    
    // More robust custom hostname detection:
    // - Not localhost or 127.0.0.1
    // - Not the default domain (if set)
    // - Not ending with default domain (for subdomains of default domain)
    // - Has at least one dot (is a domain, not just a hostname)
    const isCustomHostname = hostname !== 'localhost' && 
                            !hostname.includes('127.0.0.1') && 
                            !hostname.includes('0.0.0.0') &&
                            hostname !== defaultDomain &&
                            (defaultDomain === 'localhost' || !hostname.endsWith('.' + defaultDomain)) &&
                            hostname.includes('.') &&
                            !isBookingDomain();
    
    const isCustomHostnameRoot = isCustomHostname && (window.location.pathname === '/' || window.location.pathname === '');
    // Custom hostname event path: /{eventKey} e.g. /F4408622 or /AD926237 (single segment, no auth)
    const pathname = window.location.pathname || '';
    const pathSegments = pathname.replace(/^\/|\/$/g, '').split('/').filter(Boolean);
    const reservedPaths = ['login', 'auth', 'admin', 'joblist', 'setup', 'help', 'events', 'static', 'uploads', 'api', 'favicon.ico'];
    const isCustomHostnameEventPath = isCustomHostname && pathSegments.length === 1 && !reservedPaths.includes(pathSegments[0].toLowerCase());

    console.log('Custom hostname check:', { hostname, defaultDomain, isCustomHostname, isCustomHostnameRoot, isCustomHostnameEventPath, pathname: window.location.pathname });

    // Public routes that don't require authentication
    const isPublicRoute = isAuthCallback || isLoginPage || isPublicEventRoute || isCustomHostnameRoot || isCustomHostnameEventPath;

    // Show loading while checking authentication (unless on public routes)
    if (isAuthenticating && !isPublicRoute) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    // Always render AppRouter first to handle public routes
    // Public routes: auth callback, login, public events, custom hostnames
    if (!user || !role) {
        if (isPublicRoute) {
            // Let AppRouter handle these public routes
            return (
                <div className="min-h-screen bg-gray-100 font-sans">
                    <AppRouter 
                        role={null}
                        user={null}
                        dealerships={dealerships}
                        templates={templates}
                        dropdownOptions={dropdownOptions}
                        handleUpdateDealership={handleUpdateDealership}
                        handleTemplateUpdate={handleTemplateUpdate}
                        handleAddDealership={handleAddDealership}
                        handleDropdownUpdate={handleDropdownUpdate}
                        handlePushTemplate={handlePushTemplate}
                    />
                </div>
            );
        }
        // Not on a public route, show login
        return <Login />;
    }

    return (
        <div className="min-h-screen bg-gray-100 font-sans">
            {isLoading ? (
                <div className="flex items-center justify-center min-h-screen">
                    <LoadingSpinner />
                </div>
            ) : (
                <AppRouter 
                    role={role}
                    user={user}
                    dealerships={dealerships}
                    templates={templates}
                    dropdownOptions={dropdownOptions}
                    handleUpdateDealership={handleUpdateDealership}
                    handleTemplateUpdate={handleTemplateUpdate}
                    handleAddDealership={handleAddDealership}
                    handleDropdownUpdate={handleDropdownUpdate}
                    handlePushTemplate={handlePushTemplate}
                    onLogout={handleLogout}
                />
            )}
        </div>
    );
}

export default function App() {
    return (
        <AppContent />
    );
}